import { useAppRouter } from '@/common/route/useAppRouter';
import { useCrudActions } from '@/common/view/useCrudActions';
import { usePromotionItemAdd } from '@/modules/promotion/item/model/add';
import { DisplayType } from '@/modules/promotion/item/model/mixins';
import { ROUTE_NAME_PROMOTION } from '@/modules/promotion/router';
import { useVModal } from '@/plugins/modal/v-modal';
import { promotionService } from '@/services/promotion';
import useVuelidate from '@vuelidate/core';
import { minValue, required } from '@vuelidate/validators';
import { computed, onMounted, reactive } from 'vue';
import { useMixins } from './mixins';

export function usePromotionBoosterAdd() {
    const router = useAppRouter();

    const { fetchLinkableItems, promotionOption, boosterOption } = useMixins();

    const boosterOptionExcludeAdded = computed(() => {
        const addedTypes = model.addedBoosterList.map(item => item.type);
        return boosterOption.value.filter(item => !addedTypes.includes(item.value));
    });

    const model = reactive({
        name: null,
        promotionId: null,
        selectedBooster: null,
        quantity: 1,
        addedBoosterList: []
    });

    const validations = {
        name: { required },
        promotionId: { required },
        quantity: { required, minValue: minValue(1) },
        addedBoosterList: { required }
    };

    const v$ = useVuelidate(validations, model);

    const fetch = async () => {
        await fetchLinkableItems();
        model.selectedBooster = boosterOptionExcludeAdded.value?.[0]?.value ?? null;
    };

    const actions = {
        create: useCrudActions().excuteAndNotify({
            validator: v$,
            request: () => {
                const boosterList: Array<number> = [];
                const quantityList: Array<number> = [];
                model.addedBoosterList.forEach(item => {
                    boosterList.push(item.type);
                    quantityList.push(item.quantity);
                });

                return promotionService.booster.create(model.name, model.promotionId, boosterList, quantityList);
            },
            onSuccess: () => router.push({ name: ROUTE_NAME_PROMOTION.BOOSTER.INDEX })
        }),

        addBoosterItem: () => {
            if (v$.value.quantity.$invalid) {
                return;
            }

            const selectedItem = boosterOption.value.find(item => item.value === model.selectedBooster);
            if (selectedItem) {
                model.addedBoosterList.push({
                    type: selectedItem.value,
                    name: selectedItem.label,
                    quantity: model.quantity
                });

                model.selectedBooster = boosterOptionExcludeAdded.value?.[0]?.value ?? null;
            }
        },

        removeBoosterItem: (type: number) => {
            model.addedBoosterList.splice(
                model.addedBoosterList.findIndex(item => item.type === type),
                1
            );
            model.selectedBooster = boosterOptionExcludeAdded.value?.[0]?.value ?? null;
        }
    };

    onMounted(async () => await fetch());

    return { v$, model, fetch, fetchLinkableItems, boosterOptionExcludeAdded, promotionOption, actions };
}

export function usePromotionModalProps() {
    const vModal = useVModal();

    const MODAL_NAME = 'create-promotion-modal';
    const { fetch, model: promotionModel, v$: promotionV$ } = usePromotionItemAdd();

    const modalActions = {
        createAccessUnlimitedPromotion: async () => {
            return await promotionService.item.create(
                promotionModel.name,
                null,
                null,
                promotionModel.fromTime?.getTime(),
                promotionModel.toTime?.getTime(),
                promotionModel.displayType,
                promotionModel.expectedPackage,
                promotionModel.prototypeId,
                promotionModel.imageFile
            );
        },
        openModal: async () => {
            await fetch();
            promotionModel.displayType = DisplayType.AccessUnlimited.value;
            promotionV$.value.$reset();
            vModal.open({ name: MODAL_NAME });
        },
        closeModal: () => {
            vModal.close({ name: MODAL_NAME });
        }
    };

    return { MODAL_NAME, promotionV$, promotionModel, modalActions };
}
